(function () {
  const containers = document.querySelectorAll("[data-fw-reels]");
  if (!containers.length) return;

  const sessionId = getSessionId();

  containers.forEach((container) => {
    const cards      = Array.from(container.querySelectorAll("[data-fw-reel-card]"));
    const track      = container.querySelector("[data-fw-reels-track]");
    const scroller   = track?.parentElement;
    const dialog        = container.querySelector("[data-fw-reels-dialog]");
    let   video         = dialog?.querySelector("[data-fw-reels-video]");
    let   videoAlt      = dialog?.querySelector("[data-fw-reels-video-alt]");
    const _videoOrig    = video;
    const _videoAltOrig = videoAlt;
    const videoBg       = dialog?.querySelector("[data-fw-reels-video-bg]");
    const progressBar   = dialog?.querySelector("[data-fw-reels-progress]");
    const muteButton    = dialog?.querySelector("[data-fw-reels-mute]");
    const mobileProduct = dialog?.querySelector("[data-fw-reels-mobile-product]");
    const mobileAtcBtn  = dialog?.querySelector("[data-fw-reels-mobile-atc]");
    const prevButton  = container.querySelector("[data-fw-reels-prev]");
    const nextButton  = container.querySelector("[data-fw-reels-next]");
    const trackUrl   = container.dataset.trackUrl || "/apps/reels/track";
    const playlistId = container.dataset.playlistId || "";

    if (!cards.length || !track || !scroller) return;

    // Move dialog to <body> so position:fixed is against viewport,
    // not clipped by ancestor overflow:hidden or CSS transforms.
    // All dialog-internal queries below must use `dialog`, not `container`.
    if (dialog) document.body.appendChild(dialog);

    container.classList.toggle("is-short-list", cards.length <= 5);
    container.classList.toggle("is-single-reel", cards.length < 2);

    let activeIndex = Math.floor((cards.length - 1) / 2);
    let dialogActiveCard = null;
    let dialogActiveVariantId = null;
    let dialogMuted = true;

    // ── Card preview videos ───────────────────────────────
    cards.forEach((card) => {
      const preview = card.querySelector("video");
      if (!preview) return;
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.3) preview.play().catch(() => {});
          else preview.pause();
        });
      }, { threshold: [0, 0.3] });
      obs.observe(preview);
    });

    // ── ResizeObserver ────────────────────────────────────
    const ro = new ResizeObserver(() => {
      applyPadding();
      centerCard(activeIndex, false);
      updateStackState();
    });
    ro.observe(scroller);

    // ── Card click ────────────────────────────────────────
    cards.forEach((card, i) => {
      card.addEventListener("click", () => {
        activeIndex = i;
        updateStackState();
        openDialog(card);
      });
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activeIndex = i;
          updateStackState();
          openDialog(card);
        }
      });
    });

    prevButton?.addEventListener("click", () => goToIndex(activeIndex - 1, true, "prev"));
    nextButton?.addEventListener("click", () => goToIndex(activeIndex + 1, true, "next"));

    let scrollTimer = null;
    let programmaticScroll = false; // prevents scroll-correction from re-entering handler
    scroller.addEventListener("scroll", () => {
      if (programmaticScroll) return;
      if (scrollTimer) window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        const detected = nearestCardIndex();
        if (detected !== activeIndex) {
          activeIndex = detected;
          updateStackState();
          // CSS `order` changed — card positions shifted. Re-center so the next
          // snap aligns correctly in the new layout.
          programmaticScroll = true;
          centerCard(activeIndex, false);
          window.setTimeout(() => { programmaticScroll = false; }, 150);
        }
      }, 80);
    }, { passive: true });

    applyPadding();
    updateStackState();
    centerCard(activeIndex, false);

    // ── Impression tracking ───────────────────────────────
    const impressionObs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
        if (entry.target.dataset.impressed === "true") return;
        entry.target.dataset.impressed = "true";
        sendTrack("IMPRESSION", entry.target);
      });
    }, { threshold: [0.5] });
    cards.forEach((card) => impressionObs.observe(card));

    // ── Dialog close — query from dialog (moved to body) ──
    dialog?.querySelectorAll("[data-fw-reels-close]").forEach((btn) =>
      btn.addEventListener("click", closeDialog)
    );
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !dialog?.hasAttribute("hidden")) closeDialog();
    });

    // ── Swipe up/down to navigate reels (mobile popup) ───
    let swipeTouchStartY = 0;
    dialog?.addEventListener("touchstart", (e) => {
      swipeTouchStartY = e.touches[0].clientY;
    }, { passive: true });
    dialog?.addEventListener("touchend", (e) => {
      const dy = swipeTouchStartY - e.changedTouches[0].clientY;
      if (Math.abs(dy) < 50) return;
      navigatePopup(dy > 0 ? 1 : -1);
    }, { passive: true });

    // ── Video progress ────────────────────────────────────
    function setupProgressListener(el) {
      el?.addEventListener("timeupdate", () => {
        if (el !== video || !progressBar || !video.duration) return;
        progressBar.style.width = ((video.currentTime / video.duration) * 100) + "%";
      });
    }
    setupProgressListener(video);
    setupProgressListener(videoAlt);

    // ── Mute toggle ───────────────────────────────────────
    muteButton?.addEventListener("click", () => {
      if (!video) return;
      dialogMuted = !dialogMuted;
      applyMuteState(dialogMuted, true);
    });

    function applyMuteState(muted, shouldResume) {
      [video, videoAlt].forEach((el) => applyElementMuteState(el, muted));
      updateMuteIcon(muted);
      if (shouldResume && video) video.play().catch(() => {});
    }

    function applyElementMuteState(el, muted) {
      if (!el) return;
      el.muted = muted;
      el.defaultMuted = muted;
      el.volume = muted ? 0 : 1;
      el.dataset.fwMuted = muted ? "true" : "false";
      el.dataset.fwVolume = String(el.volume);
      if (muted) {
        el.setAttribute("muted", "");
      } else {
        el.removeAttribute("muted");
      }
    }

    function updateMuteIcon(muted) {
      if (!muteButton) return;
      muteButton.setAttribute("aria-label", muted ? "Unmute video" : "Mute video");
      muteButton.setAttribute("aria-pressed", muted ? "false" : "true");
      muteButton.dataset.fwMuted = muted ? "true" : "false";
      muteButton.innerHTML = muted
        ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>`
        : `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;
    }

    // ── Mobile ATC button ─────────────────────────────────
    mobileAtcBtn?.addEventListener("click", () => {
      const vid = mobileAtcBtn.dataset.fwAtcVid;
      if (vid) addToCart(vid, mobileAtcBtn);
      if (dialogActiveCard) sendTrack("PRODUCT_CLICK", dialogActiveCard);
    });

    // ── Popup reel navigation ─────────────────────────────
    dialog?.querySelector("[data-fw-reels-popup-prev]")?.addEventListener("click", () => navigatePopup(-1));
    dialog?.querySelector("[data-fw-reels-popup-next]")?.addEventListener("click", () => navigatePopup(1));

    // Slide-transition between videos (mobile only; instant swap on desktop)
    let isTransitioning  = false;
    let pendingDirection = 0; // queued swipe direction while a transition is in-flight

    function transitionToNewVideo(sources, poster, direction, callback) {
      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      if (!videoAlt || !isMobile) {
        video.pause();
        setVideoSources(video, sources);
        applyElementMuteState(video, dialogMuted);
        video.loop = true;
        video.currentTime = 0;
        video.play().catch(() => {});
        callback();
        return;
      }

      // If a transition is in-flight, queue this direction and bail.
      // The in-flight timeout will fire navigatePopup once it completes.
      if (isTransitioning) {
        pendingDirection = direction;
        return;
      }
      isTransitioning  = true;
      pendingDirection = 0;

      const fromY = direction > 0 ? "100%" : "-100%";
      const exitY = direction > 0 ? "-100%" : "100%";

      // Snap alt off-screen with no transition, then load sources
      videoAlt.style.transition = "none";
      videoAlt.style.transform  = `translateY(${fromY})`;
      if (poster) videoAlt.setAttribute("poster", poster);
      void videoAlt.offsetHeight; // commit snap position

      setVideoSources(videoAlt, sources);
      applyElementMuteState(videoAlt, dialogMuted);
      videoAlt.loop        = true;
      videoAlt.currentTime = 0;
      videoAlt.play().catch(() => {});

      // rAF ensures the snap frame is painted before the transition begins
      // — required on iOS Safari which batches style updates aggressively
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const ease = "transform 0.35s ease";
          video.style.transition    = ease;
          videoAlt.style.transition = ease;
          video.style.transform     = `translateY(${exitY})`;
          videoAlt.style.transform  = "translateY(0)";
        });
      });

      window.setTimeout(() => {
        video.pause();
        video.replaceChildren();
        video.style.transition = "none";
        videoAlt.style.transition = "none";
        // Explicitly park the outgoing element off-screen below so it's ready
        // for the next transition regardless of whether it has a CSS class default.
        video.style.transform = "translateY(100%)";
        [video, videoAlt] = [videoAlt, video];
        video.play().catch(() => {});
        applyMuteState(dialogMuted, false);
        isTransitioning = false;
        callback(); // activeIndex updated here

        // Fire a queued swipe that arrived while we were transitioning
        if (pendingDirection !== 0) {
          const dir    = pendingDirection;
          pendingDirection = 0;
          navigatePopup(dir);
        }
      }, 420);
    }

    function navigatePopup(direction) {
      if (cards.length < 2) return;
      const newIndex = (activeIndex + direction + cards.length) % cards.length;
      const newCard  = cards[newIndex];
      if (progressBar) progressBar.style.width = "0%";
      const newSources = parseSources(newCard.dataset.videoSources || "[]");
      const newBg      = newCard.querySelector("video")?.getAttribute("poster") || newCard.dataset.productImage || "";
      // Only update activeIndex inside the callback so a blocked transition
      // (isTransitioning guard) never leaves activeIndex pointing at the wrong card
      transitionToNewVideo(newSources, newBg, direction, () => {
        activeIndex      = newIndex;
        dialogActiveCard = newCard;
        updateStackState();
        if (videoBg) videoBg.style.backgroundImage = newBg ? `url(${newBg})` : "";
        updateMobileProduct(newCard);
        loadProductContent(newCard);
        sendTrack("PLAY", newCard);
      });
    }

    // ── Centering ─────────────────────────────────────────
    function applyPadding() {
      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      if (isMobile) {
        // 20% side padding: center card = 60vw, peek = ~17vw (~18%) each side
        const pad = Math.round(scroller.clientWidth * 0.20);
        track.style.paddingLeft  = pad + "px";
        track.style.paddingRight = pad + "px";
      } else {
        track.style.paddingLeft  = "0px";
        track.style.paddingRight = "0px";
      }
    }

    function centerCard(index, animate) {
      const card = cards[index];
      if (!card || !scroller.clientWidth) return;
      const target = card.offsetLeft - (scroller.clientWidth - card.offsetWidth) / 2;
      scroller.scrollTo({ left: Math.max(0, target), behavior: animate ? "smooth" : "instant" });
    }

    function goToIndex(index, animate, direction) {
      const isMobileLayout = window.matchMedia("(max-width: 749px)").matches;
      // On mobile, cards are in natural DOM order — FLIP animation relies on
      // `order` changes so skip it; just scroll to the card smoothly.
      const before = (animate && !isMobileLayout) ? measureCards() : null;
      activeIndex = (index + cards.length) % cards.length;
      updateStackState();
      if (before) animateCardsFrom(before, direction);
      centerCard(activeIndex, animate);
    }

    function measureCards() {
      return new Map(cards.map((card) => [card, card.getBoundingClientRect()]));
    }

    function animateCardsFrom(before, direction) {
      const isShortList = container.classList.contains("is-short-list");
      cards.forEach((card) => {
        const oldRect = before.get(card);
        if (!oldRect) return;
        const newRect = card.getBoundingClientRect();
        let deltaX = oldRect.left - newRect.left;
        if (isShortList && Math.abs(deltaX) > scroller.clientWidth / 2) {
          deltaX = direction === "next" ? scroller.clientWidth : -scroller.clientWidth;
        }
        if (!deltaX) return;
        card.style.transition = "none";
        card.style.transform = `translate3d(${deltaX}px, 0, 0)`;
      });
      requestAnimationFrame(() => {
        cards.forEach((card) => {
          if (!card.style.transform) return;
          card.style.transition = "";
          card.style.transform = "translate3d(0, 0, 0)";
        });
      });
      window.setTimeout(() => {
        cards.forEach((card) => { card.style.transform = ""; });
      }, 360);
    }

    function nearestCardIndex() {
      const center = scroller.scrollLeft + scroller.clientWidth / 2;
      let nearest = activeIndex;
      let nearestDistance = Infinity;
      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - center);
        if (distance < nearestDistance) { nearest = index; nearestDistance = distance; }
      });
      return nearest;
    }

    function updateStackState() {
      // On mobile, cards stay in natural DOM order so scroll-snap positions are stable.
      // Changing `order` on every activeIndex update shifts card positions and
      // breaks the snap points for subsequent swipes.
      const isMobileLayout = window.matchMedia("(max-width: 749px)").matches;
      cards.forEach((card, index) => {
        const offset = circularOffset(index);
        card.classList.toggle("is-active",  offset === 0);
        card.classList.toggle("is-prev",    offset === -1);
        card.classList.toggle("is-next",    offset === 1);
        card.classList.toggle("is-prev-2",  offset === -2);
        card.classList.toggle("is-next-2",  offset === 2);
        if (isMobileLayout) {
          card.classList.remove("is-hidden");
          card.style.order = "";
        } else {
          card.classList.toggle("is-hidden", Math.abs(offset) > 2);
          card.style.order = Math.abs(offset) <= 2 ? String(offset + 2) : "";
        }
      });
      if (prevButton) prevButton.disabled = cards.length < 2;
      if (nextButton) nextButton.disabled = cards.length < 2;
    }

    function circularOffset(index) {
      let offset = index - activeIndex;
      if (cards.length > 2) {
        const half = cards.length / 2;
        if (offset > half)  offset -= cards.length;
        if (offset < -half) offset += cards.length;
      }
      return offset;
    }

    // ── Mobile product card overlay ───────────────────────
    function updateMobileProduct(card) {
      if (!mobileProduct) return;
      const allProducts = parseSources(card.dataset.allProducts || "[]");
      const p = allProducts[0] || {};
      const url       = p.url       || card.dataset.productUrl   || "#";
      const image     = p.image     || card.dataset.productImage || "";
      const title     = p.title     || card.dataset.productTitle || "";
      const price     = p.price     || card.dataset.productPrice || "";
      const variantId = p.variantId || card.dataset.variantId    || "";
      mobileProduct.href = buildUtmUrl(
        url + (variantId ? "?variant=" + variantId : ""),
        card.dataset.reelId
      );
      mobileProduct.onclick = () => sendTrack("PRODUCT_CLICK", card);
      const imgEl   = mobileProduct.querySelector("[data-fw-m-img]");
      const titleEl = mobileProduct.querySelector("[data-fw-m-title]");
      const priceEl = mobileProduct.querySelector("[data-fw-m-price]");
      if (imgEl)   { imgEl.src = image; imgEl.alt = title; }
      if (titleEl) titleEl.textContent = title;
      if (priceEl) priceEl.textContent = price;
      mobileProduct.removeAttribute("hidden");
      if (mobileAtcBtn) {
        mobileAtcBtn.dataset.fwAtcVid = variantId;
        mobileAtcBtn.disabled = false;
        mobileAtcBtn.textContent = "Add to Cart";
        mobileAtcBtn.removeAttribute("hidden");
      }
    }

    // ── Dialog open / close ───────────────────────────────
    function openDialog(card) {
      if (!dialog) return;
      // Reset transition state and video refs — prior swipes may have left these dirty
      isTransitioning  = false;
      pendingDirection = 0;
      video    = _videoOrig;
      videoAlt = _videoAltOrig;
      if (videoAlt) {
        videoAlt.pause();
        videoAlt.replaceChildren();
        videoAlt.style.transition = "none";
        videoAlt.style.transform  = "translateY(100%)"; // park off-screen below
      }
      dialogActiveCard = card;
      const poster = card.querySelector("video")?.getAttribute("poster") || card.dataset.productImage || "";
      if (videoBg) videoBg.style.backgroundImage = poster ? `url(${poster})` : "";
      if (progressBar) progressBar.style.width = "0%";
      setVideoSources(video, parseSources(card.dataset.videoSources || "[]"));
      dialogMuted = true;
      applyMuteState(true, false);
      video.loop  = true;
      video.currentTime = 0;
      video.play().catch(() => {});
      updateMobileProduct(card);
      loadProductContent(card);
      dialog.removeAttribute("hidden");
      dialog.classList.remove("fw-reels__dialog--hidden");
      document.documentElement.style.overflow = "hidden";
      sendTrack("PLAY", card);
    }

    function closeDialog() {
      if (!dialog) return;
      isTransitioning  = false;
      pendingDirection = 0;
      video.pause();
      video.removeAttribute("src");
      video.replaceChildren();
      video.style.transition = "none";
      video.style.transform  = "translateY(0)";
      if (videoAlt) {
        videoAlt.pause();
        videoAlt.replaceChildren();
        videoAlt.style.transition = "none";
        videoAlt.style.transform  = "translateY(100%)"; // park off-screen below
      }
      video    = _videoOrig;
      videoAlt = _videoAltOrig;
      dialog.setAttribute("hidden", "");
      dialog.classList.add("fw-reels__dialog--hidden");
      document.documentElement.style.overflow = "";
      dialogActiveCard = null;
      dialogActiveVariantId = null;
      if (mobileProduct) mobileProduct.setAttribute("hidden", "");
      if (mobileAtcBtn) mobileAtcBtn.setAttribute("hidden", "");
    }

    // ── Add to Cart (no drawer — just add, show feedback) ─
    async function addToCart(variantId, btn) {
      if (!variantId) return;
      const originalHTML = btn ? btn.innerHTML : null;
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = '<span class="fw-atc-spinner"></span>';
      }
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || "Cart add failed");
        if (btn) btn.textContent = "Added!";
        setTimeout(() => {
          if (btn) { btn.disabled = false; btn.textContent = "Add to Cart"; }
        }, 2000);
      } catch (err) {
        console.error("[fw-reels] addToCart error:", err);
        if (btn) { btn.disabled = false; btn.innerHTML = originalHTML || "Add to Cart"; }
      }
    }

    // ── Open cart drawer (refresh content then open) ──────
    async function openCartDrawer(btn) {
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer) return;
      if (btn) btn.disabled = true;
      try {
        const res  = await fetch("/cart?section_id=cart-drawer");
        const text = await res.text();
        const parsed = new DOMParser().parseFromString(text, "text/html");
        for (const sel of ["cart-drawer-items", ".cart-drawer__footer"]) {
          const target = document.querySelector(sel);
          const source = parsed.querySelector(sel);
          if (target && source) target.replaceWith(source);
        }
        if (typeof initDrawerSwiper === "function") initDrawerSwiper();
        if (typeof updateCartCount_cs === "function") updateCartCount_cs();
      } catch (_) {}
      closeDialog();
      cartDrawer.open();
      if (btn) btn.disabled = false;
    }

    // ── Product content (async, fetches from Shopify API) ─
    async function loadProductContent(card) {
      const loadingEl = dialog.querySelector("[data-fw-reels-loading]");
      const contentEl = dialog.querySelector("[data-fw-reels-content]");
      if (!contentEl) return;

      if (loadingEl) loadingEl.hidden = false;
      contentEl.innerHTML = "";

      const allProducts = parseSources(card.dataset.allProducts || "[]")
        .filter((p) => p && p.variantId);

      if (!allProducts.length) {
        if (loadingEl) loadingEl.hidden = true;
        return;
      }

      if (allProducts.length > 1) {
        if (loadingEl) loadingEl.hidden = true;
        contentEl.innerHTML = renderMultiProduct(allProducts, card.dataset.reelId);
        contentEl.querySelectorAll("[data-fw-product-id]").forEach((btn) => {
          btn.addEventListener("click", () => {
            if (dialogActiveCard) sendTrack("PRODUCT_CLICK", dialogActiveCard);
          });
        });
        contentEl.querySelectorAll("[data-fw-pcard-atc]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const vid = btn.dataset.fwPcardAtc;
            if (vid) addToCart(vid, btn);
            if (dialogActiveCard) sendTrack("PRODUCT_CLICK", dialogActiveCard);
          });
        });
        return;
      }

      // Single product — fetch product data first, recs in background
      const p = allProducts[0];
      dialogActiveVariantId = p.variantId ? String(p.variantId) : null;
      const handle = (p.url || "").split("/products/")[1]?.split("?")[0]?.split("#")[0];

      try {
        const productData = await (handle
          ? fetch("/products/" + handle + ".js").then((r) => r.json())
          : Promise.resolve(null));

        if (loadingEl) loadingEl.hidden = true;
        contentEl.innerHTML = renderSingleProduct(productData, p, card.dataset.reelId);
        bindSingleProductEvents(contentEl, productData);

        // Recommendations load in background — append when ready
        if (p.id) {
          fetch("/recommendations/products.js?product_id=" + p.id + "&limit=2&intent=related")
            .then((r) => r.json())
            .then((recsData) => {
              const recs = recsData?.products;
              if (!recs?.length || !contentEl.isConnected) return;
              const body = contentEl.querySelector(".fw-info-body");
              if (body) body.insertAdjacentHTML("beforeend", renderRecs(recs));
            })
            .catch(() => {});
        }
      } catch (_) {
        if (loadingEl) loadingEl.hidden = true;
        contentEl.innerHTML = renderSingleProduct(null, p);
        bindSingleProductEvents(contentEl, null);
      }
    }

    function renderSingleProduct(data, fallback, reelId) {
      const title    = data?.title   || fallback.title || "";
      const url      = fallback.url  || "#";
      const rawImgs  = Array.isArray(data?.images) ? data.images : (fallback.image ? [fallback.image] : []);
      const images   = rawImgs.map((img) => (typeof img === "string" ? img : img?.src || "")).filter(Boolean);
      const variants = data?.variants || [];
      const options  = data?.options  || [];
      const firstV   = variants[0] || null;

      if (firstV) dialogActiveVariantId = String(firstV.id);

      const priceStr   = firstV ? money(firstV.price) : (fallback.price || "");
      const compareStr = firstV && firstV.compare_at_price > firstV.price
        ? money(firstV.compare_at_price) : "";

      // Image gallery: horizontal thumbnail strip only
      let galleryHtml = "";
      if (images.length > 0) {
        galleryHtml = `
          <div class="fw-info-slider">
            <div class="fw-info-thumb-strip">${images.slice(0, 6).map((img, i) =>
              `<button class="fw-info-thumb${i === 0 ? " is-active" : ""}" data-fw-thumb="${i}" data-fw-thumb-src="${img}" type="button" aria-label="Image ${i + 1}">
                <img src="${img}" alt="${esc(title)}" loading="${i === 0 ? "eager" : "lazy"}">
              </button>`
            ).join("")}</div>
          </div>`;
      }

      // Variant selector
      let varHtml = "";
      if (options.length > 0 && variants.length > 1) {
        const optName = options[0]?.name || "Option";
        varHtml = `
          <div class="fw-info-variants">
            <p class="fw-info-opt-label">${esc(optName)}:</p>
            <div class="fw-info-opt-btns">
              ${variants.map((v) => `
                <button class="fw-info-opt-btn${v === firstV ? " is-active" : ""}" type="button"
                  data-fw-vid="${v.id}" data-fw-price="${v.price}"
                  data-fw-cp="${v.compare_at_price > v.price ? v.compare_at_price : 0}">
                  ${esc(v.option1)}
                </button>`).join("")}
            </div>
          </div>`;
      }

      // Description accordion
      const descHtml = data?.description ? `
        <button class="fw-info-desc-btn" type="button" data-fw-desc-toggle>
          Description
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        <div class="fw-info-desc-body" data-fw-desc-body hidden>${data.description}</div>` : "";

      return `
        ${galleryHtml}
        <div class="fw-info-body">
          <h3 class="fw-info-title">${esc(title)}</h3>
          <div class="fw-info-prices">
            <span class="fw-info-price" data-fw-display-price>${priceStr}</span>
            <s class="fw-info-compare" data-fw-display-compare${compareStr ? "" : ' hidden'}>${compareStr}</s>
          </div>
          ${varHtml}
          <div class="fw-info-actions">
            <button class="fw-info-add-to-cart" type="button" data-fw-add-to-cart>
              Add to Cart
            </button>
            <button class="fw-info-go-to-cart" type="button" data-fw-go-to-cart>
              Go to Cart
            </button>
          </div>
          <a class="fw-info-view-more"
            href="${buildUtmUrl(url + (firstV ? '?variant=' + firstV.id : ''), reelId)}"
            data-fw-shop-link data-fw-base-url="${esc(url)}" data-fw-reel-id="${esc(reelId || '')}"
            target="_blank">
            View more
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </a>
          ${descHtml}
        </div>`;
    }

    function renderRecs(recs) {
      return `
        <p class="fw-info-recs-title">You may like</p>
        <div class="fw-info-recs-grid">
          ${recs.map((rec) => {
            const rp = rec.price_min ? money(rec.price_min) : "";
            const rc = rec.compare_at_price_min && rec.compare_at_price_min > rec.price_min
              ? money(rec.compare_at_price_min) : "";
            const ri = rec.featured_image?.src || rec.images?.[0] || "";
            return `
              <a class="fw-info-rec-card" href="${rec.url}" target="_blank">
                ${ri ? `<img class="fw-info-rec-img" src="${ri}" alt="${esc(rec.title)}" loading="lazy">` : ""}
                <div class="fw-info-rec-body">
                  <p class="fw-info-rec-name">${esc(rec.title)}</p>
                  <span class="fw-info-rec-price">${rp}</span>
                  ${rc ? `<s class="fw-info-rec-compare">${rc}</s>` : ""}
                </div>
              </a>`;
          }).join("")}
        </div>`;
    }

    function renderMultiProduct(products, reelId) {
      const items = products.map((p) => {
        const shopUrl = buildUtmUrl(
          (p.url || "#") + (p.variantId ? "?variant=" + p.variantId : ""),
          reelId
        );
        return `
        <div class="fw-info-pcard">
          <a href="${shopUrl}" target="_blank" tabindex="-1">
            ${p.image ? `<img class="fw-info-pcard-img" src="${p.image}" alt="${esc(p.title || "")}" loading="lazy">` : ""}
          </a>
          <div class="fw-info-pcard-body">
            <a class="fw-info-pcard-name-link" href="${shopUrl}" target="_blank"
              data-fw-product-id="${p.id || ""}">
              <p class="fw-info-pcard-name">${esc(p.title || "")}</p>
            </a>
            <div class="fw-info-pcard-prices">
              <span class="fw-info-pcard-price">${p.price || ""}</span>
              ${p.compare ? `<s class="fw-info-pcard-compare">${p.compare}</s>` : ""}
            </div>
          </div>
          <button class="fw-info-pcard-atc" type="button" data-fw-pcard-atc="${p.variantId || ""}">
            Add to Cart
          </button>
        </div>`;
      }).join("");

      return `
        <div class="fw-info-multi">
          <p class="fw-info-multi-title">Products Seen in the video</p>
          <div class="fw-info-multi-grid">${items}</div>
        </div>`;
    }

    function bindSingleProductEvents(el, data) {
      const priceEl  = el.querySelector("[data-fw-display-price]");
      const cmpEl    = el.querySelector("[data-fw-display-compare]");
      const variants = data?.variants || [];

      // Add to Cart — add qty 1, no drawer
      el.querySelector("[data-fw-add-to-cart]")?.addEventListener("click", (e) => {
        if (dialogActiveVariantId) addToCart(dialogActiveVariantId, e.currentTarget);
        if (dialogActiveCard) sendTrack("PRODUCT_CLICK", dialogActiveCard);
      });

      // Go to Cart — refresh drawer content then open it
      el.querySelector("[data-fw-go-to-cart]")?.addEventListener("click", (e) => {
        openCartDrawer(e.currentTarget);
      });

      // View more — track product click
      el.querySelector(".fw-info-view-more")?.addEventListener("click", () => {
        if (dialogActiveCard) sendTrack("PRODUCT_CLICK", dialogActiveCard);
      });

      // Image thumbnail strip — highlight active thumb on click
      el.querySelectorAll("[data-fw-thumb]").forEach((thumb) => {
        thumb.addEventListener("click", () => {
          el.querySelectorAll("[data-fw-thumb]").forEach((t) => t.classList.remove("is-active"));
          thumb.classList.add("is-active");
        });
      });

      // Variant selection
      el.querySelectorAll("[data-fw-vid]").forEach((btn) => {
        btn.addEventListener("click", () => {
          el.querySelectorAll("[data-fw-vid]").forEach((b) => b.classList.remove("is-active"));
          btn.classList.add("is-active");
          dialogActiveVariantId = btn.dataset.fwVid;
          if (priceEl) priceEl.textContent = money(Number(btn.dataset.fwPrice || 0));
          if (cmpEl) {
            const cp = Number(btn.dataset.fwCp || 0);
            cmpEl.textContent = cp ? money(cp) : "";
            cmpEl.hidden = !cp;
          }
          const v = variants.find((vv) => String(vv.id) === btn.dataset.fwVid);
          // Update view-more link to pre-select this variant
          const viewMore = el.querySelector("[data-fw-shop-link]");
          if (viewMore) {
            const base = viewMore.dataset.fwBaseUrl || "";
            const rId  = viewMore.dataset.fwReelId  || "";
            viewMore.href = buildUtmUrl(base + "?variant=" + btn.dataset.fwVid, rId);
          }
        });
      });

      // Description accordion
      el.querySelector("[data-fw-desc-toggle]")?.addEventListener("click", () => {
        const body = el.querySelector("[data-fw-desc-body]");
        if (body) body.hidden = !body.hidden;
      });
    }

    function sendTrack(type, card) {
      const body = new FormData();
      body.set("type", type);
      body.set("reelId", card.dataset.reelId || "");
      body.set("playlistId", playlistId);
      body.set("sessionId", sessionId);
      if (card.dataset.productId) body.set("productId", card.dataset.productId);
      if (navigator.sendBeacon) { navigator.sendBeacon(trackUrl, body); return; }
      fetch(trackUrl, { method: "POST", body, keepalive: true }).catch(() => {});
    }
  });

  // ── Shared utilities ──────────────────────────────────────
  function parseSources(value) {
    try { return JSON.parse(value) || []; } catch (_) { return []; }
  }

  function setVideoSources(video, sources) {
    video.replaceChildren();
    const playableSources = getPlayableSources(video, sources);
    video.dataset.fwSourceCount = String(playableSources.length);
    video.dataset.fwSourceType = playableSources[0]?.mimeType || "";
    video.dataset.fwSourceUrl = playableSources[0]?.url || "";
    playableSources.forEach((s) => {
      const el = document.createElement("source");
      el.src  = s.url;
      el.type = s.mimeType || "video/mp4";
      video.appendChild(el);
    });
    video.load();
  }

  function getPlayableSources(video, sources) {
    const clean = (Array.isArray(sources) ? sources : []).filter((s) => s?.url);
    if (!clean.length) return [];

    const canPlay = (mimeType) => {
      if (!mimeType) return true;
      return video.canPlayType(mimeType) !== "";
    };

    const mp4 = clean.filter((s) => {
      const type = s.mimeType || "";
      return type.includes("mp4") || /\.mp4(\?|#|$)/i.test(s.url);
    });
    const hls = clean.filter((s) => {
      const type = s.mimeType || "";
      return type.includes("mpegurl") || type.includes("x-mpegURL") || /\.m3u8(\?|#|$)/i.test(s.url);
    });
    const other = clean.filter((s) => !mp4.includes(s) && !hls.includes(s));

    const preferred = [...mp4, ...other, ...hls].filter((s) => canPlay(s.mimeType));
    return preferred.length ? preferred : clean;
  }

  function money(cents) {
    if (!cents) return "";
    return "Rs. " + (cents / 100).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function buildUtmUrl(url, reelId) {
    if (!url || url === "#") return url;
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set("utm_source", "shoppable_reels");
      u.searchParams.set("utm_medium", "video");
      u.searchParams.set("utm_campaign", "shoppable_reels");
      if (reelId) u.searchParams.set("utm_content", reelId);
      return u.toString();
    } catch (_) {
      return url;
    }
  }

  function getSessionId() {
    const key = "fw_reels_session";
    const v = window.sessionStorage.getItem(key);
    if (v) return v;
    const next = window.crypto?.randomUUID?.() ||
      (Date.now().toString(16) + Math.random().toString(16).slice(2));
    window.sessionStorage.setItem(key, next);
    return next;
  }
})();
